import { useState } from "react";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getInBody, type InBodyTeaser } from "../lib/api";
import { useHub } from "../hooks/useHub";
import ScoreRing from "../components/ScoreRing";
import clanPromoPhoto from "../assets/tasks/logo-story.jpeg";
import rowingPhoto from "../assets/zones/rowing-logo.jpg";
import LogoTile from "../components/LogoTile";
import TabBar from "../components/TabBar";
import { useT } from "../i18n";
import { errorMessage } from "../i18n/errors";

function Chevron() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path d="M9 6l6 6-6 6" stroke="#86868E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RowingIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9E84FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h4l2-7 4 14 2-7h6" />
    </svg>
  );
}

function InBodyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9E84FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="2.5" />
      <path d="M12 8v7M12 9l-5 2M12 9l5 2M9 21l3-6 3 6" />
    </svg>
  );
}

// Decorative bar-chart row for the rowing card (static / illustrative).
const ROW_BARS: { h: number; tone: "low" | "mid" | "hi" | "peak" }[] = [
  { h: 35, tone: "low" },
  { h: 52, tone: "mid" },
  { h: 44, tone: "low" },
  { h: 68, tone: "hi" },
  { h: 58, tone: "hi" },
  { h: 82, tone: "hi" },
  { h: 100, tone: "peak" },
  { h: 72, tone: "hi" },
  { h: 48, tone: "mid" },
  { h: 60, tone: "mid" },
  { h: 38, tone: "low" },
  { h: 54, tone: "low" },
];

function barClass(tone: "low" | "mid" | "hi" | "peak"): string {
  switch (tone) {
    case "low":
      return "bg-[#2E2A3D]";
    case "mid":
      return "bg-[#3A3350]";
    case "hi":
      return "bg-gradient-to-b from-[#8E6BFF] to-[#6E48F4]";
    case "peak":
      return "bg-gradient-to-b from-[#9E84FF] to-[#7C5CFC]";
  }
}

function fmt(v?: number, unit?: string): string {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  const rounded = Math.round(v * 10) / 10;
  return unit ? `${rounded} ${unit}` : String(rounded);
}

export default function Hub() {
  const nav = useNavigate();
  const { t } = useT();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const q = `?phone=${encodeURIComponent(phone)}`;

  const { hub, error, loading } = useHub(phone || null);

  const [inbody, setInbody] = useState<InBodyTeaser | null>(null);

  // Тизер InBody подгружаем один раз (он не меняется в рамках фестиваля).
  useEffect(() => {
    if (!phone) return;
    let alive = true;
    getInBody()
      .then((r) => {
        if (alive) setInbody(r);
      })
      .catch(() => {
        /* тизер некритичен — молча игнорируем */
      });
    return () => {
      alive = false;
    };
  }, [phone]);

  if (!phone) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-6 text-muted text-sm">
        {t("hub.noPhone")}
      </div>
    );
  }

  if (loading && !hub) {
    return <div className="min-h-dvh flex items-center justify-center px-6 text-muted text-sm">{t("hub.loading")}</div>;
  }

  if (error && !hub) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center px-6 text-center">
        <p className="text-red-400 text-sm">{errorMessage(error, t)}</p>
        <button
          type="button"
          onClick={() => nav("/login")}
          className="mt-4 h-12 px-6 rounded-[14px] bg-white/[0.06] text-white font-medium text-sm ring-1 ring-white/10 active:bg-white/10 transition-colors"
        >
          {t("hub.backToLogin")}
        </button>
      </div>
    );
  }

  if (!hub) return null;

  const { participant, clan, rowing } = hub;
  const hasInbody = !!inbody?.hasResult;
  // Действующий клиент HJ: Зоны 1–2 закрыты, его игра — кланы (подарочный HW).
  const isExistingClient = participant.isExistingClient ?? false;

  // Прогресс фестиваля (0–3): Зона 1 гребля, Зона 2 InBody, Зона 3 клан.
  // Приз — не зона: его статус живёт в золотой карточке ниже.
  const steps = [!!rowing, hasInbody, !!clan];
  const done = steps.filter(Boolean).length;

  // Карточка кланов: для действующих клиентов HJ показывается первой (это их
  // единственная игра), для новых — после Зон 1–2. Бейдж «Зона 3» — только новым.
  const clanSection = clan ? (
          <button
            type="button"
            onClick={() => nav(`/clan${q}`)}
            className="relative w-full text-left flex items-center gap-3.5 bg-violet-card rounded-[22px] p-4 overflow-hidden shadow-violet-lg active:opacity-95 transition-opacity"
          >
            <span className="absolute -right-[30px] -bottom-[50px] w-[120px] h-[120px] rounded-full bg-white/10" />
            <span className="relative w-[42px] h-[42px] rounded-[12px] bg-white/[0.16] flex items-center justify-center text-2xl shrink-0">
              {clan.emoji || "🛡️"}
            </span>
            <div className="relative flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[16px] font-extrabold text-white truncate">{clan.name}</span>
                <span className="text-[10px] font-bold text-white bg-white/[0.18] rounded-full px-2 py-0.5 shrink-0">
                  {clan.code}
                </span>
              </div>
              <div className="text-[13px] text-white/80 mt-1">
                {t("clan.list.tasks", { done: clan.tasksDone, total: clan.tasksTotal })} ·{" "}
                {t("clan.list.members", { count: clan.memberCount })}
              </div>
            </div>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="relative shrink-0">
              <path d="M9 6l6 6-6 6" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <section className="relative bg-violet-card rounded-[22px] p-5 overflow-hidden shadow-violet-lg">
            {/* Фото задания как фон + фиолетовая заливка, чтобы текст читался */}
            <img src={clanPromoPhoto} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(124,92,252,0.55) 0%, rgba(98,56,238,0.88) 55%, rgba(91,52,232,0.96) 100%)" }}
            />
            <span className="absolute -right-[30px] -bottom-[50px] w-[150px] h-[150px] rounded-full bg-white/10" />
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-[19px] font-extrabold text-white">{t("hub.clan.title")}</h2>
                {!isExistingClient && (
                  <span className="text-[11px] font-bold text-white bg-white/[0.16] px-2 py-0.5 rounded-md whitespace-nowrap">
                    {t("stations.zonePrefix", { n: 3 })}
                  </span>
                )}
              </div>
              <p className="text-white/85 text-sm leading-[1.55] mb-[18px]">
                {t(isExistingClient ? "hub.clan.bodyGift" : "hub.clan.body")}
              </p>
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={() => nav(`/clan/create${q}`)}
                  className="flex-1 h-[50px] rounded-[14px] bg-white text-violet-deepest font-extrabold text-[15px] active:opacity-90 transition-opacity"
                >
                  {t("hub.clan.create")}
                </button>
                <button
                  type="button"
                  onClick={() => nav(`/clan/join${q}`)}
                  className="flex-1 h-[50px] rounded-[14px] bg-white/10 ring-1 ring-white/45 text-white font-bold text-[15px] active:bg-white/20 transition-colors"
                >
                  {t("hub.clan.join")}
                </button>
              </div>
            </div>
          </section>
        );

  return (
    <div className="min-h-dvh max-w-md mx-auto px-[22px] pt-4 pb-28">
      {/* Header */}
      <header className="flex items-center gap-3 pt-1.5 mb-3.5">
        <LogoTile size={46} radius={15} fontSize={16} />
        <div className="flex-1 min-w-0">
          <h1 className="text-[21px] font-extrabold tracking-tight leading-[1.1]">
            {t("hub.greeting", { name: participant.nickname || t("hub.greetingFallback") })}
          </h1>
          <p className="text-[13px] text-muted mt-[3px]">{t("hub.subtitle")}</p>
        </div>
        <span className="flex items-center gap-1.5 bg-success/[0.12] border border-success/25 px-2.5 py-1.5 rounded-full shrink-0">
          <span
            className="w-[7px] h-[7px] rounded-full bg-success"
            style={{ boxShadow: "0 0 8px #3FCF6A", animation: "liveDot 1.8s ease-in-out infinite" }}
          />
          <span className="text-xs font-bold text-success">{t("hub.live")}</span>
        </span>
      </header>

      <div className="flex flex-col gap-3.5">
        {/* Progress card — действующим клиентам не показываем: их игра только кланы */}
        {!isExistingClient && (
          <section className="bg-card border border-line-2 rounded-[18px] px-4 py-3.5">
            <div className="flex items-center justify-between mb-[11px]">
              <span className="text-[13px] font-bold text-white">{t("hub.progress.title")}</span>
              <span className="text-xs font-bold text-violet-lighter">{t("hub.progress.count", { done })}</span>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`flex-1 h-1.5 rounded-[3px] ${
                    i < done ? "bg-gradient-to-r from-[#8E6BFF] to-[#6E48F4]" : "bg-line-2"
                  }`}
                />
              ))}
            </div>
          </section>
        )}

        {/* Действующий клиент HJ: кланы — первыми, Зоны 1–2 закрыты */}
        {isExistingClient && (
          <>
            {clanSection}

            <section className="relative bg-card-2-grad border border-line-3 rounded-[22px] p-5 overflow-hidden opacity-70">
              <img src={rowingPhoto} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, rgba(12,12,14,0.6) 0%, rgba(12,12,14,0.92) 100%)" }}
              />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="w-[34px] h-[34px] rounded-[10px] bg-violet-t16 flex items-center justify-center">
                    <RowingIcon />
                  </span>
                  <h2 className="text-[18px] font-extrabold text-white">{t("hub.rowing.title")}</h2>
                </div>
                <span className="text-xs font-bold text-violet-lighter bg-violet-t14 px-[9px] py-1 rounded-lg whitespace-nowrap shrink-0">
                  🔒 {t("hub.rowing.zone")}
                </span>
              </div>
              <p className="relative text-sm text-muted leading-relaxed mt-3">{t("hub.zoneLocked.rowing")}</p>
              <p className="relative text-[13px] text-violet-lighter font-medium leading-[1.45] mt-2">
                {t("hub.zoneLocked.friend")}
              </p>
            </section>

            <section className="relative flex items-center gap-3.5 bg-card border border-line-2 rounded-[18px] p-4 opacity-70">
              <span className="absolute top-4 right-4 text-[11px] font-bold text-violet-lighter bg-violet-t14 px-2 py-0.5 rounded-md whitespace-nowrap">
                🔒 {t("stations.zonePrefix", { n: 2 })}
              </span>
              <span className="w-[42px] h-[42px] rounded-[12px] bg-violet-t14 flex items-center justify-center shrink-0">
                <InBodyIcon />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[16px] font-extrabold text-white pr-14">{t("hub.inbody.title")}</div>
                <div className="text-[13px] text-muted mt-0.5 leading-[1.4]">{t("hub.zoneLocked.inbody")}</div>
                <div className="text-[13px] text-violet-lighter font-medium mt-1.5 leading-[1.45]">
                  {t("hub.zoneLocked.friend")}
                </div>
              </div>
            </section>
          </>
        )}

        {/* Зона 1 — гребля: тап открывает мой результат + live-лидерборд */}
        {!isExistingClient && (
        <button
          type="button"
          onClick={() => nav(`/rowing${q}`)}
          className="relative w-full text-left bg-card-2-grad border border-line-3 rounded-[22px] p-5 overflow-hidden active:opacity-95 transition-opacity"
        >
          {/* Фото гребли под тёмной заливкой — цифры результата остаются читаемыми */}
          <img src={rowingPhoto} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(180deg, rgba(12,12,14,0.45) 0%, rgba(12,12,14,0.82) 60%, rgba(12,12,14,0.92) 100%)" }}
          />
          <span
            className="absolute -right-10 -top-10 w-[140px] h-[140px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(124,92,252,0.3), transparent 70%)" }}
          />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <span className="w-[34px] h-[34px] rounded-[10px] bg-violet-t16 flex items-center justify-center">
                <RowingIcon />
              </span>
              <h2 className="text-[18px] font-extrabold text-white">{t("hub.rowing.title")}</h2>
            </div>
            <span className="text-xs font-bold text-violet-lighter bg-violet-t14 px-[9px] py-1 rounded-lg whitespace-nowrap shrink-0">
              {t("hub.rowing.zone")}
            </span>
          </div>

          {rowing ? (
            <>
              <div className="relative flex items-end gap-1.5 mb-[5px]">
                <span className="font-display text-[52px] font-bold text-white leading-[0.9]">
                  {Math.round(rowing.distance_m)}
                </span>
                <span className="text-[18px] text-muted font-semibold mb-[9px]">{t("unit.meters")}</span>
              </div>
              <div className="relative text-[13px] text-muted mb-4">
                {(() => {
                  // Split the localized template on a sentinel and render the
                  // bold power value as real JSX (no dangerouslySetInnerHTML).
                  const [before, after = ""] = t("hub.rowing.metric", {
                    power: "{{P}}",
                  }).split("{{P}}");
                  return (
                    <>
                      {before}
                      <span className="text-white font-bold">{fmt(rowing.power_w)}</span>
                      {after}
                    </>
                  );
                })()}
              </div>
              <div className="relative flex items-end gap-1 h-[42px]">
                {ROW_BARS.map((bar, i) => (
                  <div
                    key={i}
                    className={`flex-1 rounded-[2px] ${barClass(bar.tone)}`}
                    style={{ height: `${bar.h}%` }}
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="relative text-sm text-muted leading-relaxed">{t("hub.rowing.empty")}</p>
          )}
          <p className="relative text-[12px] text-violet-lighter font-medium mt-4">{t("stations.rowing.prize")}</p>
        </button>
        )}

        {/* Зона 2 — InBody */}
        {!isExistingClient && (
        <button
          type="button"
          onClick={() => nav(`/inbody${q}`)}
          className="relative w-full text-left flex items-center gap-3.5 bg-card border border-line-2 rounded-[18px] p-4 active:bg-white/[0.03] transition-colors"
        >
          <span className="absolute top-4 right-4 text-[11px] font-bold text-violet-lighter bg-violet-t14 px-2 py-0.5 rounded-md whitespace-nowrap">
            {t("stations.zonePrefix", { n: 2 })}
          </span>
          <span className="w-[42px] h-[42px] rounded-[12px] bg-violet-t14 flex items-center justify-center shrink-0">
            <InBodyIcon />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-extrabold text-white pr-14">{t("hub.inbody.title")}</div>
            {hasInbody ? (
              <div className="flex items-center gap-4 mt-2">
                <ScoreRing score={inbody?.score ?? 0} size={64} />
                <div>
                  {inbody?.metabolicAge != null && (
                    <>
                      <div className="text-muted text-xs">{t("inbody.metabolicAge")}</div>
                      <div className="text-base font-semibold mt-0.5">{Math.round(inbody.metabolicAge)}</div>
                    </>
                  )}
                  <div className="text-muted text-xs mt-1.5">{t("hub.inbody.pbf")}</div>
                  <div className="text-base font-semibold mt-0.5 blur-[4px] select-none">
                    {fmt(inbody?.pbf, t("unit.percent"))}
                  </div>
                  <p className="text-violet-lighter text-[13px] mt-1.5 font-medium">{t("hub.inbody.cta")}</p>
                </div>
              </div>
            ) : (
              <div className="text-[13px] text-muted mt-0.5 leading-[1.4]">{t("hub.inbody.empty")}</div>
            )}
          </div>
          <Chevron />
        </button>
        )}

        {/* Зона 3 — клан (для действующих клиентов рендерится выше, первым) */}
        {!isExistingClient && clanSection}
      </div>

      <TabBar active="home" phone={phone} />
    </div>
  );
}
